import React, { memo, useMemo } from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { Button, Card } from "@/shared";
import { exportToCsv } from "../../utils/reportUtils";
import { BreakdownTable } from "../InvoiceAnalyticsPanel";
import {
  buildProfitLossCsvRows,
  buildProfitLossKpis,
  buildProfitLossMetricSections,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatPeriod,
} from "./profitLossFormatters";
import { downloadProfitLossPdf } from "./profitLossPdf";

/**
 * Money cell that turns red when negative.
 * @param {{ value: number }} props
 */
const SignedMoney = ({ value }) => (
  <span className={value < 0 ? "text-red-600" : undefined}>
    {formatMoney(value)}
  </span>
);

/**
 * ProfitLossReport
 * Company Profit / Loss view: headline KPIs, the statement (revenue, labor,
 * expenses, profit), key metrics, and month / client / employee breakdowns.
 * Includes PDF and CSV export.
 *
 * @param {object} props
 * @param {object} props.report - Profit / Loss response (`reportType: "profitLoss"`)
 * @param {string} [props.reportName="Profit / Loss Report"]
 * @param {{ name?: string }} [props.parentCompany] - Used in the PDF header
 * @param {boolean} [props.loading=false]
 * @param {Function} [props.onRefresh] - Regenerates the report when provided
 * @returns {JSX.Element|null}
 */
export const ProfitLossReport = ({
  report,
  reportName = "Profit / Loss Report",
  parentCompany,
  loading = false,
  onRefresh,
}) => {
  const summary = report?.summary || {};
  const kpis = useMemo(() => buildProfitLossKpis(summary), [summary]);
  const sections = useMemo(
    () => buildProfitLossMetricSections(summary),
    [summary],
  );

  if (!report?.summary) return null;

  const statement = report.data || [];
  const byMonth = report.byMonth || [];
  const byClient = report.byClient || [];
  const byEmployee = report.byEmployee || [];
  const byExpenseCategory = report.byExpenseCategory || [];
  const include = report.filters?.include || {};

  const handlePdf = () => {
    try {
      downloadProfitLossPdf({ report, reportName, parentCompany });
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating profit / loss PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const handleCsv = () => {
    exportToCsv(buildProfitLossCsvRows(report), "profit-loss-report");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{reportName}</h3>
          <p className="text-sm text-gray-500">
            {formatPeriod(report.period)} ·{" "}
            {report.filters?.invoiceStatus === "paid"
              ? "Paid invoices"
              : "All invoices"}{" "}
            ·{" "}
            {report.filters?.payoutStatus === "completed"
              ? "Completed payouts"
              : "All payouts"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePdf}
            className="flex items-center"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCsv}
            className="flex items-center"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            CSV
          </Button>
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center"
            >
              <ArrowPathIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {include.expenses && !report.expensesAvailable && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          Expense tracking isn't set up yet, so expenses show as $0 and net
          profit equals gross profit.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.key}>
            <div className="text-xs text-gray-500">{kpi.label}</div>
            <div
              className={classNames("text-lg font-semibold tabular-nums", {
                "text-red-600": kpi.tone === "negative",
                "text-green-700": kpi.tone === "positive",
                "text-gray-900": !kpi.tone,
              })}
            >
              {kpi.value}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Card title="Statement" className="xl:col-span-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                <th className="py-2 px-2 font-semibold">Line</th>
                <th className="py-2 px-2 font-semibold text-right">Amount</th>
                <th className="py-2 px-2 font-semibold text-right">
                  % of revenue
                </th>
              </tr>
            </thead>
            <tbody>
              {statement.map((row, index) => {
                const startsSection =
                  index === 0 || statement[index - 1].section !== row.section;
                return (
                  <React.Fragment key={row.key}>
                    {startsSection && row.kind === "line" && (
                      <tr>
                        <td
                          colSpan={3}
                          className="pt-3 pb-1 px-2 text-xs font-semibold uppercase text-gray-500"
                        >
                          {row.section}
                        </td>
                      </tr>
                    )}
                    <tr
                      className={classNames("border-b border-gray-100", {
                        "font-semibold": row.kind !== "line",
                        "bg-gray-50": row.kind === "total",
                      })}
                    >
                      <td
                        className={classNames("py-1.5 px-2", {
                          "pl-6": row.kind === "line",
                        })}
                      >
                        {row.label}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums">
                        <SignedMoney value={row.amount} />
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                        {formatPercent(row.percentOfRevenue)}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>

        <div className="space-y-3">
          {sections.map((section) => (
            <Card key={section.id} title={section.title}>
              <dl className="text-sm divide-y divide-gray-100">
                {section.rows.map((row) => (
                  <div key={row.label} className="flex justify-between py-1">
                    <dt className="text-gray-600">{row.label}</dt>
                    <dd className="font-medium tabular-nums">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
        </div>
      </div>

      {byMonth.length > 0 && (
        <Card title="By month">
          <BreakdownTable
            headers={[
              "Month",
              "Revenue",
              "Labor",
              "Expenses",
              "Gross profit",
              "Net profit",
              "Net margin",
            ]}
            rows={byMonth.map((row) => [
              formatMonth(row.month),
              formatMoney(row.revenue),
              formatMoney(row.laborCost),
              formatMoney(row.expenseCost),
              <SignedMoney key="gross" value={row.grossProfit} />,
              <SignedMoney key="net" value={row.netProfit} />,
              formatPercent(row.netMargin),
            ])}
          />
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {byClient.length > 0 && (
          <Card title="Revenue by client">
            <BreakdownTable
              headers={["Client", "Invoices", "Revenue", "% of revenue", "Outstanding"]}
              rows={byClient.map((row) => [
                row.clientName,
                formatNumber(row.invoiceCount),
                formatMoney(row.revenue),
                formatPercent(row.percentOfRevenue),
                formatMoney(row.outstanding),
              ])}
            />
          </Card>
        )}
        {byEmployee.length > 0 && (
          <Card title="Labor by employee">
            <BreakdownTable
              headers={["Employee", "Payouts", "Total", "% of labor", "Miles", "Hours"]}
              rows={byEmployee.map((row) => [
                row.employeeName,
                formatNumber(row.payoutCount),
                formatMoney(row.total),
                formatPercent(row.percentOfLabor),
                formatNumber(row.miles),
                formatNumber(row.hours),
              ])}
            />
          </Card>
        )}
        {byExpenseCategory.length > 0 && (
          <Card title="Expenses by category">
            <BreakdownTable
              headers={["Category", "Count", "Amount", "% of expenses"]}
              rows={byExpenseCategory.map((row) => [
                row.category,
                formatNumber(row.count),
                formatMoney(row.amount),
                formatPercent(row.percentOfExpenses),
              ])}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default memo(ProfitLossReport);
